import { Box, Button, IconButton, styled, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocUpload from "../Scan/components/DocUpload";
import { Row } from "../../components/FlexComponents";
import { SegmentedChoice } from "../../components/Settlement/settlementStyles";

// A required either/or in place of the Reason field (the covenant challenge's Score / Document).
// Each option may carry its own description placeholder.
export type BlockLandChoice = {
  label: string;
  options: { value: string; label: string; placeholder?: string }[];
};

type BlockLandFormProps = {
  reason: string;
  setReason: (reason: string) => void;
  showReason?: boolean;
  choice?: BlockLandChoice;
  choiceValue?: string | null;
  setChoiceValue?: (value: string) => void;
  descriptionRequired?: boolean;
  showFiles?: boolean;
  description: string;
  setDescription: (description: string) => void;
  files: File[];
  MAX_FILES: number;
  pendingFile: File | undefined;
  setPendingFile: (file: File | undefined) => void;
  handleAddFile: () => void;
  handleRemoveFile: (index: number) => void;
  isMobile: boolean;
  isReadOnly?: boolean;
};

const BlockLandForm = ({
  reason,
  setReason,
  showReason = true,
  choice,
  choiceValue = null,
  setChoiceValue,
  descriptionRequired = false,
  showFiles = true,
  description,
  setDescription,
  files,
  MAX_FILES,
  pendingFile,
  setPendingFile,
  handleAddFile,
  handleRemoveFile,
  isMobile,
  isReadOnly = false,
}: BlockLandFormProps) => {
  const descriptionPlaceholder =
    choice?.options.find((option) => option.value === choiceValue)?.placeholder ?? "Enter detailed description";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mb: "16px" }}>
      {choice && (
        <Box>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "black",
              mb: "8px",
            }}
          >
            {choice.label} *
          </Typography>
          <SegmentedChoice
            options={choice.options}
            value={choiceValue}
            onChange={(value) => setChoiceValue?.(value)}
            disabled={isReadOnly}
          />
        </Box>
      )}

      {showReason && (
        <Box>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "black",
              mb: "8px",
            }}
          >
            Reason *
          </Typography>
          <StyledTextField
            fullWidth
            placeholder="Enter reason for blocking"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
          />
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            color: "black",
            mb: "8px",
            textTransform: "uppercase",
          }}
        >
          Description{descriptionRequired ? " *" : ""}
        </Typography>
        <StyledTextField
          fullWidth
          placeholder={descriptionPlaceholder}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          size="small"
          disabled={isReadOnly}
          InputProps={isReadOnly ? { readOnly: true } : undefined}
        />
      </Box>

      <Box>
        {!isReadOnly && showFiles && (
          <>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 500,
                color: "black",
                mb: "8px",
                textTransform: "uppercase",
              }}
            >
              Evidence (Document/Image/Video)
            </Typography>

            {files.length > 0 && (
              <Row
                sx={{
                  flexWrap: "wrap",
                  gap: { xs: "8px", sm: "10px" },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: "12px",
                }}
              >
                {files.map((file, index) => (
                  <FileContainer key={index} sx={{ width: { xs: "100%", sm: "fit-content" } }}>
                    <FileLink sx={{ maxWidth: { xs: "100%", sm: "200px" } }}>
                      <FileName>{file.name}</FileName>
                    </FileLink>
                    <IconButton
                      size="small"
                      sx={{ position: "absolute", right: 8, top: 8 }}
                      onClick={() => handleRemoveFile(index)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </FileContainer>
                ))}
              </Row>
            )}

            {files.length < MAX_FILES && (
              <Row
                sx={{
                  gap: { xs: "8px", sm: "10px" },
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  width: "100%",
                }}
              >
                <DocUpload
                  acceptImages
                  isFileDeleted={!pendingFile}
                  style={{
                    margin: 0,
                    marginRight: { xs: 0, sm: "10px" },
                    height: { xs: "auto", sm: "56px" },
                    minHeight: { xs: "80px", sm: "56px" },
                    width: "100%",
                  }}
                  setFile={setPendingFile}
                  title={`Select evidence file (${files.length}/${MAX_FILES})`}
                  uploadButton={
                    <Button
                      sx={{
                        height: "max-content",
                        width: { xs: "100%", sm: "100%" },
                        mt: { xs: 1, sm: 1 },
                        fontSize: { xs: "12px", sm: "14px" },
                        textTransform: "none",
                      }}
                      size={isMobile ? "small" : "small"}
                      disabled={!pendingFile}
                      onClick={handleAddFile}
                    >
                      Add File
                    </Button>
                  }
                />
              </Row>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    fontFamily: "'Inter', sans-serif",
    "& fieldset": {
      borderColor: "#E0E0E0",
    },
    "&:hover fieldset": {
      borderColor: "#BDBDBD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#000",
    },
  },
  "& .MuiInputBase-input": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "14px",
  },
});

const FileContainer = styled(Box)({
  position: "relative",
  margin: "8px 4px",
  width: "fit-content",
});

const FileLink = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  border: `2px dashed ${theme.palette.action.disabled}`,
  background: theme.palette.background.paper,
  padding: "12px 36px 12px 12px",
  display: "flex",
  alignItems: "center",
  color: "#3d3d3d",
  textDecoration: "none",
  minWidth: 120,
  maxWidth: 200,
  [theme.breakpoints.down("sm")]: {
    padding: "10px 36px 10px 10px",
    minWidth: 100,
  },
}));

const FileName = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 150,
  display: "block",
  "@media (max-width: 400px)": {
    maxWidth: 120,
  },
});

export default BlockLandForm;
